import { CSSProperties } from "react";
import { styled, Theme } from "@mui/material/styles";
import { Box, Button, Typography } from "@mui/material";

/* ---------- Root layout (ported from TenantUI TimeSliceView.styled.ts) ---------- */

export const DetailsViewRoot = styled(Box)(() => ({
    display: "flex",
    flexDirection: "column",
    height: "100%",
}));

export const DetailsHeader = styled(Box)(({ theme }) => ({
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(2),
    padding: theme.spacing(3, 4),
    borderBottom: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.background.paper,
}));

export const DetailsTitle = styled(Typography)(() => ({
    fontSize: "1.25rem",
    fontWeight: 700,
    color: "#111827",
    display: "flex",
    alignItems: "center",
    gap: 12,
}));

export const ContentWithSidebar = styled(Box)(() => ({
    display: "flex",
    flex: 1,
    overflow: "hidden",
    minHeight: 0,
}));

export const DetailsContent = styled(Box)(({ theme }) => ({
    flex: 1,
    padding: theme.spacing(3),
    overflow: "auto",
    minHeight: 0,
}));

export const NotificationBanner = styled(Box, {
    shouldForwardProp: (prop) => prop !== "isWarning",
})<{ isWarning?: boolean }>(({ theme, isWarning }) => ({
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.5),
    padding: theme.spacing(1.5, 4),
    fontSize: "0.875rem",
    backgroundColor: isWarning ? "#fffbeb" : theme.palette.grey[100],
    borderBottom: `1px solid ${isWarning ? "#fde68a" : theme.palette.divider}`,
    color: isWarning ? "#9f592c" : theme.palette.text.secondary,
    flexShrink: 0,
}));

/* ---------- Status badge ---------- */

export const StyledStatusBadge = styled("span", {
    shouldForwardProp: (prop) => prop !== "isActive",
})<{ isActive?: boolean }>(({ isActive }) => ({
    padding: "2px 6px",
    fontSize: "10px",
    fontWeight: 700,
    borderRadius: 4,
    border: "1px solid",
    textTransform: "uppercase",
    letterSpacing: "0.03em",
    whiteSpace: "nowrap" as const,
    flexShrink: 0,
    ...(isActive
        ? { backgroundColor: "#dcfce7", color: "#166534", borderColor: "#bbf7d0" }
        : { backgroundColor: "#f3f4f6", color: "#6b7280", borderColor: "#e5e7eb" }),
}));

/* ---------- Action buttons ---------- */

const baseButtonStyles = (theme: Theme): CSSProperties => ({
    borderRadius: 8,
    fontSize: "0.75rem",
    fontWeight: 500,
    textTransform: "none" as const,
    boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
    whiteSpace: "nowrap" as const,
    padding: theme.spacing(0.75, 1.5),
});

export const EditButton = styled(Button, {
    shouldForwardProp: (prop) => prop !== "isEditing",
})<{ isEditing?: boolean }>(({ theme, isEditing }) => ({
    ...baseButtonStyles(theme),
    backgroundColor: isEditing ? `${theme.palette.primary.main}14` : theme.palette.background.paper,
    border: `1px solid ${isEditing ? `${theme.palette.primary.main}40` : theme.slate[300]}`,
    color: isEditing ? theme.palette.primary.main : theme.slate[700],
    "&:hover": {
        backgroundColor: isEditing ? `${theme.palette.primary.main}1F` : theme.slate[50],
        borderColor: isEditing ? `${theme.palette.primary.main}40` : theme.slate[300],
    },
}));

export const ConfirmButton = styled(Button)(({ theme }) => ({
    ...baseButtonStyles(theme),
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    "&:hover": {
        backgroundColor: theme.palette.primary.dark,
    },
    "&.Mui-disabled": {
        backgroundColor: theme.slate[200],
        color: theme.slate[400],
    },
}));

export const DiscardButton = styled(Button)(({ theme }) => ({
    ...baseButtonStyles(theme),
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${theme.slate[300]}`,
    color: theme.slate[700],
    "&:hover": {
        backgroundColor: theme.slate[50],
        borderColor: theme.slate[400],
    },
}));

/* ---------- Sidebar (ported from TenantUI Sidebar.styled.ts) ---------- */

export const SidebarRoot = styled(Box)(({ theme }) => ({
    width: 300,
    backgroundColor: theme.palette.grey[50],
    borderRight: `1px solid ${theme.palette.divider}`,
    display: "flex",
    flexDirection: "column",
    overflowY: "auto",
    height: "100%",
    flexShrink: 0,
}));

export const SectionContainer = styled(Box)(({ theme }) => ({
    padding: theme.spacing(2, 1.5),
}));

export const SectionTitle = styled(Typography)(({ theme }) => ({
    fontSize: "0.75rem",
    fontWeight: 600,
    color: theme.slate[500],
    textTransform: "uppercase",
    letterSpacing: "0.05em",
    paddingLeft: 8,
    paddingRight: 8,
}));

export const SectionDivider = styled(Box)(({ theme }) => ({
    borderTop: `1px solid ${theme.palette.divider}`,
    marginLeft: theme.spacing(2),
    marginRight: theme.spacing(2),
}));

export const NavButton = styled("button", {
    shouldForwardProp: (prop) => prop !== "isSelected",
})<{ isSelected: boolean }>(({ theme, isSelected }) => ({
    width: "100%",
    display: "flex",
    alignItems: "center",
    gap: 12,
    padding: "8px 12px",
    fontSize: "0.875rem",
    fontWeight: 500,
    fontFamily: theme.typography.fontFamily,
    borderRadius: 8,
    border: "none",
    cursor: "pointer",
    transition: "background-color 150ms ease, color 150ms ease, box-shadow 150ms ease",
    textAlign: "left" as const,
    backgroundColor: isSelected ? "#fff" : "transparent",
    color: isSelected ? theme.palette.primary.main : theme.slate[600],
    boxShadow: isSelected ? "0 1px 4px 0 rgba(0,0,0,0.06)" : "none",
    outline: isSelected ? `1px solid ${theme.palette.divider}` : "none",
    "&:hover": {
        backgroundColor: isSelected ? "#fff" : theme.slate[100],
        color: isSelected ? theme.palette.primary.main : theme.slate[900],
    },
}));

export const ValidationDot = styled("span", {
    shouldForwardProp: (prop) => prop !== "isValid",
})<{ isValid: boolean }>(({ theme, isValid }) => ({
    width: 8,
    height: 8,
    borderRadius: "50%",
    backgroundColor: isValid ? theme.palette.success.main : theme.palette.error.main,
    marginLeft: "auto",
    flexShrink: 0,
    display: "inline-block",
}));
